/* eslint-disable max-lines */
import { gql } from '@apollo/client/core'

import {
  ADDRESS_ATTRIBUTES,
  PAGE_INFO_ATTRIBUTES,
  RENTAL_BASE_ATTRIBUTES,
  RENTAL_STATUS_ATTRIBUTES
} from '../fragments'

export const FETCH_RENTAL_PRODUCTS = gql`
  query fetchProducts(
    $id: ID!
    $lineItemIds: [ID!]
    $lineItemType: [LineItemTypeEnum!]
    $scopes: [LineItemScope!]
    $period: [ProductPeriodEnum!]
    $sort: [SortOptions!]
    $includeSubItems: Boolean = false
  ) {
    lineItems(
      rentalId: $id
      lineItemIds: $lineItemIds
      lineItemType: $lineItemType
      scopes: $scopes
      period: $period
      includeSubItems: $includeSubItems
      sort: $sort
    ) {
      id
      name
      lineItemType
      startDate
      returnDate
      checkupEnabled
      project {
        address {
          ...addressAttributes
        }
      }
      ... on LineItem {
        vacant
        machineStatus
        imageUrl
        earliestVacantDate
        category {
          checkupDays
        }
        categorySegmentId
      }
      ... on SubItem {
        vacant
        machineStatus
        freeAttachment
        lineItemId
        categorySegmentId
        earliestVacantDate
      }
    }
    rental(id: $id) {
      id
      project {
        name
      }
    }
  }
  ${ADDRESS_ATTRIBUTES}
`

export const PAGINATED_RENTAL_QUERY = gql`
  query rentals(
    $first: Int
    $last: Int
    $offset: Int
    $after: String
    $before: String
    $query: String
    $isRequested: Boolean
    $runningRentals: Boolean
    $excludeCanceled: Boolean
    $scope: String
    $projectId: ID
    $sort: [SortOptions!] = [{ sortField: "created_at", sortDirection: "desc" }]
  ) {
    rentals(
      first: $first
      last: $last
      offset: $offset
      after: $after
      before: $before
      query: $query
      isRequested: $isRequested
      runningRentals: $runningRentals
      excludeCanceled: $excludeCanceled
      scope: $scope
      projectId: $projectId
      sort: $sort
    ) {
      nodes {
        ...rentalStatusAttributes
        ...rentalBaseAttributes
        displayPrice
        project {
          name
        }
        products {
          name
        }
      }
      pageInfo {
        ...pageInfoAttributes
      }
      totalCount
      maxPageSize
      pageIndex
    }
  }
  ${PAGE_INFO_ATTRIBUTES}
  ${RENTAL_STATUS_ATTRIBUTES}
  ${RENTAL_BASE_ATTRIBUTES}
`

export const RENTAL_COUNT_QUERY = gql`
  query rentals($isRequested: Boolean, $scope: String) {
    rentals(isRequested: $isRequested, scope: $scope) {
      totalCount
      maxPageSize
    }
  }
`

export const FETCH_RENTAL_ADDRESS = gql`
  query fetchRentalAddress($id: ID!) {
    rental(id: $id) {
      id
      deliveryAddress {
        ...addressAttributes
      }
    }
  }
  ${ADDRESS_ATTRIBUTES}
`

export const RENTAL_SHOW_QUERY = gql`
  query fetchRental($id: ID!, $omitStatusWhenCanceled: Boolean = false) {
    rental(id: $id, omitStatusWhenCanceled: $omitStatusWhenCanceled) {
      ...rentalStatusAttributes
      ...rentalBaseAttributes
      longitude
      latitude
      priceNetTotal
      priceTaxTotal
      priceProducts
      priceInsurance
      priceDelivery
      priceAdditional
      discountPrice
      priceGrossTotal
      priceGrossTotalWithDeposit
      totalPaid
      deposit
      quotedOrPast
      damagesCount
      requestableServicesEnabled
      contact {
        name
      }
      deliveryAddress {
        ...addressAttributes
      }
      managerContact {
        name
      }
      deliveryContact {
        name
      }
      confirmationSnapshot {
        url
      }
      lastQuote {
        url
        valid
        deposit
        depositPaid
        discountPrice
        grossTotal
        grossTotalWithDeposit
        netTotal
        openAmount
        priceAdditional
        priceDelivery
        priceInsurance
        pricePaid
        priceProducts
        tax
      }
      customerOrderNumber
      project {
        id
        name
        address {
          ...addressAttributes
        }
      }
      productsWithSubItems {
        ... on LineItemInterface {
          id
          name
          categorySegment {
            name
            quantityMandatory
          }
          startDate
          returnDate
          startTime
          returnTime
          discountedUnitPrice
          filterStatus
          checkupEnabled
          discountAsPercentage
          displayDiscountedUnitPrice
          displayUnitWithQuantity
          validDisplayUnit
          quantity
          total
          unit {
            id
            name
          }
          lineItemType
          requestStatus
          dummyDate
          quantityEstimated
        }
        ... on ProductLineItemInterface {
          machineStatus
        }
        ... on SubItem {
          id
          lineItemId
          name
          freeAttachment
        }
      }
      deliveries {
        name
        cargos {
          name
        }
        deliveryDate
        discountedUnitPrice
      }
    }
  }
  ${RENTAL_STATUS_ATTRIBUTES}
  ${ADDRESS_ATTRIBUTES}
  ${RENTAL_BASE_ATTRIBUTES}
`

export const RENTAL_LINE_ITEMS_QUERY = gql`
  query fetchRentalLineItems($id: ID!) {
    rental(id: $id) {
      id
      priceNetTotal
      priceGrossTotal
      priceTaxTotal
      deliveryMethod
      lineItems {
        id
        name
        discountedUnitPrice
        lineItemType
        lineItemId
        total
        quantity
        unit {
          name
        }
      }
      deliveries {
        name
        cargos {
          name
        }
        total
        discountedUnitPrice
      }
    }
  }
`

export const RENTAL_LAST_QUOTE_PANEL_QUERY = gql`
  query fetchRentalLastQuotePanel($id: ID!) {
    rental(id: $id) {
      id
      lastQuote {
        url
        deposit
        depositPaid
        discountPrice
        grossTotal
        grossTotalWithDeposit
        netTotal
        openAmount
        pricePaid
        tax
        invoiceItems {
          id
          name
          quantity
          displayQuantity
          unitPrice
          netTotal
          displayUnit {
            id
            name
          }
          unit {
            id
            name
          }
        }
      }
    }
  }
`

export const FETCH_RENTAL_FOR_DAMAGES = gql`
  query fetchRentalBase($id: ID!) {
    rental(id: $id) {
      id
      rentalCode
      project {
        name
      }
    }
  }
`

export const CANCEL_REQUEST = gql`
  mutation cancelRequest($input: CancelRequestInput!) {
    cancelRequest(input: $input) {
      errors {
        message
      }
    }
  }
`
